/**
 * Entrees de coffre : identifiant, classement, revision et chiffrement des
 * secrets (delegue a `@klavox/crypto-core`). Ce module ne voit un secret en
 * clair que pendant l'appel a `encryptVaultItem`/`decryptVaultItem` : le
 * reste du temps, seule la forme `EncryptedVaultItem` (ciphertext opaque +
 * metadonnees claires) circule et peut etre synchronisee.
 *
 * Metadonnees CLAIRES (`docs/ARCHITECTURE.md` §2) : `id`, `type`, `folderId`,
 * `favorite`, `revision`, `createdAt`, `updatedAt`, `deletedAt`. JAMAIS l'URL,
 * l'identifiant, le mot de passe ni les champs personnalises : ceux-ci vivent
 * uniquement dans `secrets`, chiffre. Ces metadonnees sont claires mais
 * AUTHENTIFIEES (liees a l'AEAD, `aad.ts`).
 */
import {
  decryptEntry,
  encryptEntry,
  type EncryptedEntry,
  type EntrySecrets,
  type SecretKey,
} from '@klavox/crypto-core';
import {
  assertBoolean,
  assertCount,
  assertId,
  assertNullableCount,
  assertNullableId,
  openWithLegacyFallback,
  recordAad,
} from './aad.js';
import { resolveNow } from './clock.js';
import { InvalidVaultModelInputError } from './errors.js';
import { generateId } from './ids.js';

export const VAULT_ITEM_TYPES = ['login', 'note', 'card', 'identity', 'ssh-key', 'wifi', 'bank', 'wallet', 'database', 'api-key', 'software-license', 'server', 'email', 'membership', 'medical'] as const;
export type VaultItemType = (typeof VAULT_ITEM_TYPES)[number];

export function isVaultItemType(value: unknown): value is VaultItemType {
  return typeof value === 'string' && (VAULT_ITEM_TYPES as readonly string[]).includes(value);
}

/** Metadonnees non secretes, communes a l'entree en clair et chiffree. */
export interface VaultItemMetadata {
  readonly id: string;
  readonly type: VaultItemType;
  readonly folderId: string | null;
  readonly favorite: boolean;
  readonly revision: number;
  readonly createdAt: number;
  readonly updatedAt: number;
  /** Horodatage de suppression (tombstone) ; `null` tant que l'entree est active. */
  readonly deletedAt: number | null;
}

/** Entree dechiffree : `secrets` en clair, a garder en memoire uniquement. */
export interface VaultItem<T extends EntrySecrets = EntrySecrets> extends VaultItemMetadata {
  readonly secrets: T;
}

/** Entree telle que stockee/synchronisee : `secrets` est un blob opaque. */
export interface EncryptedVaultItem extends VaultItemMetadata {
  readonly secrets: EncryptedEntry;
}

export interface CreateVaultItemInput<T extends EntrySecrets = EntrySecrets> {
  readonly type: VaultItemType;
  readonly secrets: T;
  readonly folderId?: string | null;
  readonly favorite?: boolean;
  readonly now?: number;
}

function assertVaultItemMetadata(item: unknown): asserts item is VaultItemMetadata {
  if (item === null || typeof item !== 'object') {
    throw new InvalidVaultModelInputError('item: objet attendu');
  }
  const candidate = item as Partial<VaultItemMetadata>;
  if (typeof candidate.id !== 'string' || candidate.id.length === 0) {
    throw new InvalidVaultModelInputError('item.id: chaine non vide attendue');
  }
}

/** Cree une nouvelle entree (revision 1, non supprimee). */
export function createVaultItem<T extends EntrySecrets = EntrySecrets>(
  input: CreateVaultItemInput<T>,
): VaultItem<T> {
  if (input === null || typeof input !== 'object') {
    throw new InvalidVaultModelInputError('input: objet attendu');
  }
  if (!isVaultItemType(input.type)) {
    throw new InvalidVaultModelInputError(`type: type d'entree inconnu (${String(input.type)})`);
  }
  if (input.secrets === null || typeof input.secrets !== 'object') {
    throw new InvalidVaultModelInputError('secrets: objet attendu');
  }
  const now = resolveNow(input.now);
  return {
    id: generateId(),
    type: input.type,
    folderId: input.folderId ?? null,
    favorite: input.favorite ?? false,
    revision: 1,
    createdAt: now,
    updatedAt: now,
    deletedAt: null,
    secrets: input.secrets,
  };
}

/**
 * Valide strictement les metadonnees et les recopie champ par champ (un champ
 * inconnu ajoute par le serveur n'est jamais propage).
 */
function strictVaultItemMetadata(item: unknown): VaultItemMetadata {
  if (item === null || typeof item !== 'object') {
    throw new InvalidVaultModelInputError('item: objet attendu');
  }
  const c = item as Record<keyof VaultItemMetadata, unknown>;
  assertId(c.id, 'item.id');
  if (!isVaultItemType(c.type)) throw new InvalidVaultModelInputError('item.type: type inconnu');
  assertNullableId(c.folderId, 'item.folderId');
  assertBoolean(c.favorite, 'item.favorite');
  assertCount(c.revision, 'item.revision');
  assertCount(c.createdAt, 'item.createdAt');
  assertCount(c.updatedAt, 'item.updatedAt');
  assertNullableCount(c.deletedAt, 'item.deletedAt');
  return {
    id: c.id,
    type: c.type,
    folderId: c.folderId,
    favorite: c.favorite,
    revision: c.revision,
    createdAt: c.createdAt,
    updatedAt: c.updatedAt,
    deletedAt: c.deletedAt,
  };
}

function vaultItemAad(m: VaultItemMetadata): string {
  return recordAad('item', [m.id, m.type, m.folderId, m.favorite, m.revision, m.createdAt, m.updatedAt, m.deletedAt]);
}

export interface EncryptVaultItemInput<T extends EntrySecrets = EntrySecrets> {
  readonly item: VaultItem<T>;
  readonly vaultKey: SecretKey;
}

/**
 * Chiffre les `secrets` de l'entree sous la `vaultKey`, avec TOUTES ses
 * metadonnees liees en donnees associees (format v2, `aad.ts`) : un serveur
 * malveillant ne peut ni substituer le blob d'une autre entree, ni modifier
 * type, dossier, favori, revision, horodatages ou tombstone sans detection.
 */
export function encryptVaultItem<T extends EntrySecrets = EntrySecrets>(
  input: EncryptVaultItemInput<T>,
): EncryptedVaultItem {
  const metadata = strictVaultItemMetadata(input?.item);
  // `entryId` = AAD canonique complete (le coeur crypto la lie telle quelle).
  const ciphertext = encryptEntry({ entry: input.item.secrets, vaultKey: input.vaultKey, entryId: vaultItemAad(metadata) });
  return { ...metadata, secrets: ciphertext };
}

export interface DecryptVaultItemInput {
  readonly item: EncryptedVaultItem;
  readonly vaultKey: SecretKey;
  /**
   * Accepte aussi le format v1 (AAD = `id` seul, metadonnees NON
   * authentifiees). Reserve a la migration des donnees locales ; ne jamais
   * l'activer pour des enregistrements recus d'un serveur ou d'un fichier.
   */
  readonly acceptLegacyFormat?: boolean;
}

/**
 * Dechiffre une entree.
 *
 * @throws {import('@klavox/crypto-core').DecryptionFailedError} mauvaise
 *   `vaultKey`, blob altere, blob substitue depuis une autre entree, ou
 *   metadonnee modifiee depuis le chiffrement
 * @throws {InvalidVaultModelInputError} metadonnees mal formees
 */
export function decryptVaultItem<T extends EntrySecrets = EntrySecrets>(
  input: DecryptVaultItemInput,
): VaultItem<T> {
  const metadata = strictVaultItemMetadata(input?.item);
  const open = (entryId: string) => () =>
    decryptEntry<T>({ ciphertext: input.item.secrets, vaultKey: input.vaultKey, entryId });
  const plaintext = openWithLegacyFallback(input.acceptLegacyFormat, open(vaultItemAad(metadata)), open(metadata.id));
  return { ...metadata, secrets: plaintext };
}

/** Remplace les secrets d'une entree et fait progresser sa revision. */
export function updateVaultItemSecrets<T extends EntrySecrets = EntrySecrets>(
  item: VaultItem<T>,
  secrets: T,
  now?: number,
): VaultItem<T> {
  assertVaultItemMetadata(item);
  if (secrets === null || typeof secrets !== 'object') {
    throw new InvalidVaultModelInputError('secrets: objet attendu');
  }
  return { ...item, secrets, revision: item.revision + 1, updatedAt: resolveNow(now) } as VaultItem<T>;
}

/** Deplace une entree vers un autre dossier (`null` = racine du coffre). */
export function moveVaultItem<T extends VaultItemMetadata>(item: T, folderId: string | null, now?: number): T {
  assertVaultItemMetadata(item);
  if (item.folderId === folderId) return item;
  return { ...item, folderId, revision: item.revision + 1, updatedAt: resolveNow(now) } as T;
}

/** Marque/demarque une entree comme favorite. */
export function setVaultItemFavorite<T extends VaultItemMetadata>(item: T, favorite: boolean, now?: number): T {
  assertVaultItemMetadata(item);
  if (item.favorite === favorite) return item;
  return { ...item, favorite, revision: item.revision + 1, updatedAt: resolveNow(now) } as T;
}

/**
 * Suppression logique (tombstone) : l'entree garde son ciphertext mais porte
 * `deletedAt`, pour que la synchro propage la suppression aux autres
 * appareils avant purge definitive.
 */
export function markVaultItemDeleted<T extends VaultItemMetadata>(item: T, now?: number): T {
  assertVaultItemMetadata(item);
  const at = resolveNow(now);
  if (item.deletedAt !== null) return item;
  return { ...item, deletedAt: at, revision: item.revision + 1, updatedAt: at } as T;
}

/** Annule une suppression logique tant que l'entree n'a pas ete purgee. */
export function restoreVaultItem<T extends VaultItemMetadata>(item: T, now?: number): T {
  assertVaultItemMetadata(item);
  if (item.deletedAt === null) return item;
  return { ...item, deletedAt: null, revision: item.revision + 1, updatedAt: resolveNow(now) } as T;
}
